import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import {
  Layers,
  Feather,
  Hammer,
  Thermometer,
  Volume2,
  Wrench,
  Timer,
  Truck,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import { Reveal, SectionLabel } from "../components/Motion";

const GROUPS = [
  {
    id: "nos-chapes",
    label: "Nos chapes",
    intro:
      "Chape traditionnelle, chape allégée ou béton dur : nos chapes s'adaptent à tous les types de construction, en neuf comme en rénovation.",
    services: [
      {
        id: "chape-traditionnelle",
        icon: Layers,
        title: "Chape traditionnelle",
        intro:
          "Le grand classique du bâtiment. Mortier de ciment, sable et eau dosé avec rigueur, taloché à la main pour offrir un support stable, plan et durable.",
        bullets: [
          "Adaptée à tous types de revêtements (carrelage, parquet, résine)",
          "Excellente durabilité et résistance mécanique",
          "Idéale pour le neuf comme pour la rénovation lourde",
        ],
      },
      {
        id: "chape-allegee",
        icon: Feather,
        title: "Chape allégée",
        intro:
          "Une solution pensée pour soulager les structures existantes. À base de billes ou agrégats légers, elle réduit la charge tout en assurant planéité et confort.",
        bullets: [
          "Idéale pour rénovation, étages anciens, planchers bois",
          "Réduit la charge sur la structure",
          "Combinable avec une isolation thermique ou phonique",
        ],
      },
      {
        id: "beton-dur",
        icon: Hammer,
        title: "Béton dur",
        intro:
          "Le sol des espaces qui travaillent. Béton lissé à haute résistance, conçu pour les ateliers, garages, entrepôts et zones de forte sollicitation.",
        bullets: [
          "Forte résistance mécanique et à l'usure",
          "Finition lisse, anti-poussière et facile d'entretien",
          "Adapté aux locaux professionnels et industriels",
        ],
      },
      {
        id: "rampe-acces",
        icon: Hammer,
        title: "Rampe d'accès garage",
        intro:
          "Rampes béton coulées sur place, rainurées à la main pour garantir l'adhérence des véhicules par tous les temps. Pente régulière, intégration des évacuations d'eau et raccord net avec la voirie.",
        bullets: [
          "Surface rainurée antidérapante",
          "Béton dimensionné au passage de véhicules",
          "Intégration des grilles et avaloirs",
        ],
      },
    ],
  },
  {
    id: "nos-isolations",
    label: "Nos isolations",
    intro:
      "Spécialiste de l'isolation thermique et phonique des sols. Nos techniques optimisent l'enveloppe de votre maison ou de votre bâtiment.",
    services: [
      {
        id: "isolation-thermique",
        icon: Thermometer,
        title: "Isolation thermique",
        intro:
          "Un sol bien isolé, c'est une maison qui consomme moins. Nous posons les complexes isolants sous chape pour garantir une enveloppe thermique performante.",
        bullets: [
          "Compatible avec la plupart des systèmes sous chape",
          "Réduction des ponts thermiques au sol",
          "Conforme aux exigences de la RE2020",
        ],
      },
      {
        id: "isolation-phonique",
        icon: Volume2,
        title: "Isolation phonique",
        intro:
          "Pour des intérieurs apaisés et conformes à la réglementation acoustique. Mise en œuvre de sous-couches résilientes performantes contre les bruits de chocs.",
        bullets: [
          "Réduction efficace des bruits d'impact",
          "Solutions conformes à la NRA",
          "Combinable avec l'isolation thermique",
        ],
      },
    ],
  },
  {
    id: "nos-solutions",
    label: "Nos solutions",
    intro:
      "Nos solutions répondent à tout type de contrainte sur les chantiers : rénovation, délais courts, locaux à fort passage.",
    services: [
      {
        id: "renovation-sols",
        icon: Wrench,
        title: "Rénovation des sols",
        intro:
          "Reprise et mise à niveau de planchers existants : ragréage, ravoirage, recouvrement d'anciens revêtements, remise en état avant pose.",
        bullets: [
          "Diagnostic et préparation du support",
          "Mise à niveau précise des sols dégradés",
          "Compatible avec planchers bois et béton",
        ],
      },
      {
        id: "sechage-rapide",
        icon: Timer,
        title: "Séchage rapide",
        intro:
          "Quand le calendrier presse, nous mettons en œuvre des chapes à séchage rapide qui permettent de raccourcir significativement les délais avant la pose du revêtement.",
        bullets: [
          "Reprise du chantier accélérée",
          "Idéal pour les délais serrés",
          "Compatible avec la plupart des revêtements",
        ],
      },
      {
        id: "sols-trafic",
        icon: Truck,
        title: "Sols à trafic intense",
        intro:
          "Pour les locaux soumis à de fortes sollicitations : ateliers, garages, entrepôts, parkings. Sols dimensionnés pour durer.",
        bullets: [
          "Forte résistance à l'usure et aux chocs",
          "Adapté au passage de véhicules et engins",
          "Finitions techniques sur mesure",
        ],
      },
    ],
  },
];

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [hash]);

  return (
    <div data-testid="page-services">
      <SEO
        title="Prestations — Chape, béton, isolation, parquet, résine"
        description="Toutes les prestations Euro Chape à Mulhouse : chape traditionnelle, chape allégée, béton dur, rampe d'accès, isolation thermique et phonique, parquet, résine moquette de pierre."
        keywords="chape traditionnelle Mulhouse, chape allégée, béton dur, isolation phonique sol, isolation thermique sol, parquet Mulhouse, résine moquette de pierre Alsace"
        canonical="https://eurochape.com/services"
      />
      {/* Header */}
      <section className="pt-32 md:pt-40 pb-20 px-6 md:px-12 lg:px-24 bg-[#EBEBE6] border-b border-[#D5D5D0]">
        <Reveal>
          <SectionLabel>Nos services</SectionLabel>
          <h1 className="font-display font-black uppercase tracking-tighter mt-8 text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.85] text-[#1A2D7E]">
            Le sol,
            <br />
            <span className="text-[#F08722]">huit expertises.</span>
          </h1>
          <p className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-[#1A2D7E]">
            Chapes, isolations et solutions techniques. Euro Chape couvre
            l'ensemble de la mise en œuvre des sols pour vos chantiers en
            Alsace et Territoire de Belfort.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            {GROUPS.map((g) => (
              <a
                key={g.id}
                href={`#${g.id}`}
                className="px-5 py-2 text-xs uppercase tracking-[0.16em] font-semibold border border-[#1A2D7E] text-[#1A2D7E] hover:bg-[#1A2D7E] hover:text-white transition-colors"
              >
                {g.label}
              </a>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Groups */}
      {GROUPS.map((group, gi) => (
        <section
          key={group.id}
          id={group.id}
          className={`px-6 md:px-12 lg:px-24 py-20 md:py-28 ${
            gi % 2 === 1 ? "bg-[#EBEBE6] border-y border-[#D5D5D0]" : ""
          }`}
        >
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
              <div className="lg:col-span-5">
                <SectionLabel>{`/ 0${gi + 1}`}</SectionLabel>
                <h2 className="font-display font-black uppercase tracking-tighter mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#1A2D7E]">
                  {group.label}
                </h2>
              </div>
              <div className="lg:col-span-6 lg:col-start-7">
                <p className="text-lg sm:text-xl font-light leading-relaxed text-[#1A2D7E]">
                  {group.intro}
                </p>
              </div>
            </div>
          </Reveal>

          <div className="space-y-8">
            {group.services.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06}>
                <article
                  id={s.id}
                  data-testid={`service-${s.id}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 bg-white border border-[#D5D5D0] p-8 md:p-12 scroll-mt-28"
                >
                  <div className="lg:col-span-4">
                    <div className="text-xs font-mono text-[#F08722]">
                      / 0{i + 1}
                    </div>
                    <s.icon
                      className="w-9 h-9 text-[#1A2D7E] mt-5"
                      strokeWidth={1.5}
                    />
                    <h3 className="font-display font-bold uppercase tracking-tighter mt-6 text-2xl sm:text-3xl leading-[0.98] text-[#1A2D7E]">
                      {s.title}
                    </h3>
                  </div>
                  <div className="lg:col-span-8">
                    <p className="text-base sm:text-lg font-light leading-relaxed text-[#1A2D7E]">
                      {s.intro}
                    </p>
                    <ul className="mt-8 space-y-3">
                      {s.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-3 text-base text-[#1A2D7E]"
                        >
                          <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#F08722] flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#1A2D7E] text-white text-center">
        <Reveal>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.9]">
            Un projet ?
            <br />
            <span className="text-[#F08722]">Parlons-en.</span>
          </h2>
          <Link
            to="/devis"
            data-testid="services-cta"
            className="mt-10 inline-flex items-center gap-3 bg-[#F08722] text-white px-10 py-5 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-white hover:text-[#1A2D7E] transition-colors"
          >
            Demander un devis <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
